import { useState } from "react";
import JobApplyModal from "./JobApplyModal";
import JobEditModal from "./JobEditModal";

interface Job {
  id: string;
  title: string;
  company: string;
  companyDesc: string;
  logo: string;
  coverImage: string;
  location: string;
  type: string;
  salary: string;
  category: string;
  description: string;
  requirements: string[];
  benefits: string[];
  posted: string;
  verified: boolean;
  urgent: boolean;
  applicants: number;
  views: number;
}

interface JobDetailDrawerProps {
  job: Job;
  onClose: () => void;
  onUpdate: (updated: Job) => void;
  viewAs: "boss" | "employee";
}

export default function JobDetailDrawer({ job, onClose, onUpdate, viewAs }: JobDetailDrawerProps) {
  const [showApply, setShowApply] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [applied, setApplied] = useState(false);

  return (
    <>
      {showApply && (
        <JobApplyModal
          jobTitle={job.title}
          company={job.company}
          onClose={() => { setShowApply(false); setApplied(true); }}
        />
      )}
      {showEdit && (
        <JobEditModal
          job={job}
          onClose={() => setShowEdit(false)}
          onSave={(updated) => { onUpdate(updated as Job); setShowEdit(false); }}
        />
      )}

      <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
        <div
          className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cover Image */}
          <div className="relative h-48 flex-shrink-0">
            <img src={job.coverImage} alt={job.title} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/40 transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
            {job.urgent && (
              <span className="absolute top-4 left-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                Urgent Hire
              </span>
            )}
          </div>

          <div className="p-6">
            {/* Company + Title */}
            <div className="flex items-start gap-4 mb-5">
              <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0">
                <img src={job.logo} alt={job.company} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-extrabold text-gray-900 leading-tight mb-1">{job.title}</h2>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-gray-600 font-medium">{job.company}</span>
                  {job.verified && <i className="ri-shield-check-fill text-green-500 text-sm" />}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-orange-500 text-sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Location</p>
                  <p className="text-xs font-bold text-gray-800">{job.location}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-green-500 text-sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Salary</p>
                  <p className="text-xs font-bold text-gray-800">{job.salary}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center bg-sky-100 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-sky-500 text-sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Job Type</p>
                  <p className="text-xs font-bold text-gray-800">{job.type}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                  <i className="ri-user-line text-amber-500 text-sm" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Applicants</p>
                  <p className="text-xs font-bold text-gray-800">{job.applicants} applied</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-5">
              <h3 className="text-sm font-extrabold text-gray-900 mb-2">Job Description</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{job.description}</p>
            </div>

            {/* Requirements */}
            <div className="mb-5">
              <h3 className="text-sm font-extrabold text-gray-900 mb-3">Requirements</h3>
              <ul className="space-y-2">
                {job.requirements.map((req, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-orange-500 text-xs" />
                    </div>
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="mb-5">
              <h3 className="text-sm font-extrabold text-gray-900 mb-3">Benefits & Perks</h3>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map((b, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs bg-green-50 text-green-700 border border-green-100 px-3 py-1.5 rounded-full font-medium">
                    <i className="ri-gift-line" />{b}
                  </span>
                ))}
              </div>
            </div>

            {/* About Company */}
            <div className="mb-6 bg-gray-50 rounded-xl p-4 border border-gray-100">
              <h3 className="text-sm font-extrabold text-gray-900 mb-2">About {job.company}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{job.companyDesc}</p>
            </div>

            {/* Action Buttons */}
            {viewAs === "employee" ? (
              <div className="space-y-3">
                {applied ? (
                  <div className="w-full py-3.5 bg-green-50 border border-green-200 text-green-700 text-sm font-bold rounded-xl flex items-center justify-center gap-2">
                    <i className="ri-check-double-line" /> Application Submitted!
                  </div>
                ) : (
                  <button
                    onClick={() => setShowApply(true)}
                    className="w-full py-3.5 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-send-plane-line" /> Apply Now — Send Resume
                  </button>
                )}
                <button className="w-full py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  <i className="ri-bookmark-line" /> Save Job
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => setShowEdit(true)}
                  className="w-full py-3.5 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-edit-line" /> Edit Job Description
                </button>
                <div className="grid grid-cols-2 gap-3">
                  <button className="py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
                    <i className="ri-user-line" /> {job.applicants} Applicants
                  </button>
                  <button className="py-3 border border-red-100 text-red-500 text-sm font-semibold rounded-xl hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
                    <i className="ri-close-circle-line" /> Close Listing
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
